/**
 * All the node types we have in the UI and that can be rendered with react-flow
 * Note: They have an N:1 mapping to the NodeTypes mapping from flow-api
 * @enum
 */
export enum NODE_TYPE {
  AI_NODE_V2 = "ai_node_v2",
  AGENT_NODE = "agent_node",
  INPUT_NODE = "input_node",
  CODE_NODE = "code_node",
  OUTPUT_NODE = "output_node",
  SPLIT_BRANCH_NODE_V2 = "split_branch_node_v2",
  SPLIT_MERGE_NODE = "split_merge_node",
  INTEGRATION_NODE = "data_integration_node",
  SPLIT_NODE_V2 = "split_node_v2",
  RULE_NODE_V2 = "rule_node_v2",
  ML_NODE = "ml_node",
  MANIFEST_CONNECTION_NODE = "manifest_connection_node",
  CUSTOM_CONNECTION_NODE = "custom_connection_node",
  WEBHOOK_CONNECTION_NODE = "webhook_connection_node",
  SQL_DATABASE_CONNECTION_NODE = "sql_database_connection_node",
  DECISION_TABLE_NODE = "decision_table_node",
  MATRIX_TABLE_NODE = "matrix_table_node",
  ASSIGNMENT_NODE = "assignment_node",
  SCORECARD_NODE = "scorecard_node",
  GROUP_NODE = "group_node",
  GROUP_PRE_SEPARATOR_NODE = "group_pre_separator_node",
  GROUP_POST_SEPARATOR_NODE = "group_post_separator_node",
  FLOW_NODE = "flow_node",
  REVIEW_CONNECTION_NODE = "review_connection_node",
  LOOP_NODE = "loop_node",
  CASE_CREATION_NODE = "case_creation_node",
  CASE_UPDATE_NODE = "case_update_node",
  READ_ENTITY_NODE = "read_entity_node",
  WRITE_ENTITY_NODE = "write_entity_node",
}

export type StructuralNodeType =
  | NODE_TYPE.INPUT_NODE
  | NODE_TYPE.OUTPUT_NODE
  | NODE_TYPE.SPLIT_BRANCH_NODE_V2
  | NODE_TYPE.SPLIT_MERGE_NODE
  | NODE_TYPE.GROUP_NODE
  | NODE_TYPE.GROUP_PRE_SEPARATOR_NODE
  | NODE_TYPE.GROUP_POST_SEPARATOR_NODE;

export const STRUCTURAL_NODE_TYPES: StructuralNodeType[] = [
  NODE_TYPE.INPUT_NODE,
  NODE_TYPE.OUTPUT_NODE,
  NODE_TYPE.SPLIT_BRANCH_NODE_V2,
  NODE_TYPE.SPLIT_MERGE_NODE,
  NODE_TYPE.GROUP_NODE,
  NODE_TYPE.GROUP_PRE_SEPARATOR_NODE,
  NODE_TYPE.GROUP_POST_SEPARATOR_NODE,
] as const;

export type MappedNodeType = Exclude<NODE_TYPE, StructuralNodeType>;

// Note: BE_MAPPED_NODE_TYPES are all types that a React Flow node can have (NODE_TYPE)
// that are also node type in flow api
export const BE_MAPPED_NODE_TYPES = [
  NODE_TYPE.AI_NODE_V2,
  NODE_TYPE.AGENT_NODE,
  NODE_TYPE.INPUT_NODE,
  NODE_TYPE.CODE_NODE,
  NODE_TYPE.OUTPUT_NODE,
  NODE_TYPE.SPLIT_BRANCH_NODE_V2,
  NODE_TYPE.SPLIT_MERGE_NODE,
  NODE_TYPE.INTEGRATION_NODE,
  NODE_TYPE.MANIFEST_CONNECTION_NODE,
  NODE_TYPE.SPLIT_NODE_V2,
  NODE_TYPE.RULE_NODE_V2,
  NODE_TYPE.ML_NODE,
  NODE_TYPE.CUSTOM_CONNECTION_NODE,
  NODE_TYPE.WEBHOOK_CONNECTION_NODE,
  NODE_TYPE.SQL_DATABASE_CONNECTION_NODE,
  NODE_TYPE.DECISION_TABLE_NODE,
  NODE_TYPE.MATRIX_TABLE_NODE,
  NODE_TYPE.ASSIGNMENT_NODE,
  NODE_TYPE.SCORECARD_NODE,
  NODE_TYPE.FLOW_NODE,
  NODE_TYPE.REVIEW_CONNECTION_NODE,
  NODE_TYPE.LOOP_NODE,
  NODE_TYPE.CASE_CREATION_NODE,
  NODE_TYPE.CASE_UPDATE_NODE,
  NODE_TYPE.READ_ENTITY_NODE,
  NODE_TYPE.WRITE_ENTITY_NODE,
] as const;

export type BeMappedNodeType = (typeof BE_MAPPED_NODE_TYPES)[number];

/**
 * All the node types that are directly creatable by the user except the connect nodes and entity nodes
 */
export const SIMPLE_CREATABLE_NODE_TYPES = [
  NODE_TYPE.AGENT_NODE,
  NODE_TYPE.ASSIGNMENT_NODE,
  NODE_TYPE.SPLIT_NODE_V2,
  NODE_TYPE.RULE_NODE_V2,
  NODE_TYPE.SCORECARD_NODE,
  NODE_TYPE.DECISION_TABLE_NODE,
  NODE_TYPE.MATRIX_TABLE_NODE,
  NODE_TYPE.AI_NODE_V2,
  NODE_TYPE.ML_NODE,
  NODE_TYPE.CODE_NODE,
  NODE_TYPE.FLOW_NODE,
  NODE_TYPE.LOOP_NODE,
] as const;

export type SimpleCreatableNodeType =
  (typeof SIMPLE_CREATABLE_NODE_TYPES)[number];

/**
 * Node types whose icon needs no provider/resource information, i.e. every
 * type `NodeTypeIcon` can render from the bare type. Connection nodes are
 * excluded because their icon is the provider's logo.
 */
export const PROVIDERLESS_ICON_NODE_TYPES = [
  ...SIMPLE_CREATABLE_NODE_TYPES,
  NODE_TYPE.REVIEW_CONNECTION_NODE,
  NODE_TYPE.CASE_CREATION_NODE,
  NODE_TYPE.CASE_UPDATE_NODE,
  NODE_TYPE.READ_ENTITY_NODE,
  NODE_TYPE.WRITE_ENTITY_NODE,
] as const;

export type ProviderlessIconNodeType =
  (typeof PROVIDERLESS_ICON_NODE_TYPES)[number];

export const isProviderlessIconNodeType = (
  value: string,
): value is ProviderlessIconNodeType =>
  (PROVIDERLESS_ICON_NODE_TYPES as readonly string[]).includes(value);

export const CREATABLE_GROUPED_NODE_TYPES = [
  NODE_TYPE.READ_ENTITY_NODE,
  NODE_TYPE.WRITE_ENTITY_NODE,
] as const;

export type CreatableGroupedNodeType =
  (typeof CREATABLE_GROUPED_NODE_TYPES)[number];

/**
 * All the node types that are currently deactivated or hidden behind a feature gate
 */
export const getInactiveNodeTypes = (featureGates: {
  agentsEnabled: boolean;
}): SimpleCreatableNodeType[] => {
  const inactiveNodes: SimpleCreatableNodeType[] = [];

  if (!featureGates.agentsEnabled) {
    inactiveNodes.push(NODE_TYPE.AGENT_NODE);
  }

  return inactiveNodes;
};

/**
 * All the node types that are shown on the graph
 */
export const NAMED_NODE_TYPES = [
  NODE_TYPE.AI_NODE_V2,
  NODE_TYPE.AGENT_NODE,
  NODE_TYPE.INPUT_NODE,
  NODE_TYPE.CODE_NODE,
  NODE_TYPE.OUTPUT_NODE,
  NODE_TYPE.INTEGRATION_NODE,
  NODE_TYPE.SPLIT_NODE_V2,
  NODE_TYPE.SPLIT_BRANCH_NODE_V2,
  NODE_TYPE.RULE_NODE_V2,
  NODE_TYPE.ML_NODE,
  NODE_TYPE.MANIFEST_CONNECTION_NODE,
  NODE_TYPE.CUSTOM_CONNECTION_NODE,
  NODE_TYPE.WEBHOOK_CONNECTION_NODE,
  NODE_TYPE.SQL_DATABASE_CONNECTION_NODE,
  NODE_TYPE.DECISION_TABLE_NODE,
  NODE_TYPE.ASSIGNMENT_NODE,
  NODE_TYPE.SCORECARD_NODE,
  NODE_TYPE.FLOW_NODE,
  NODE_TYPE.REVIEW_CONNECTION_NODE,
  NODE_TYPE.LOOP_NODE,
  NODE_TYPE.CASE_CREATION_NODE,
  NODE_TYPE.CASE_UPDATE_NODE,
  NODE_TYPE.MATRIX_TABLE_NODE,
  NODE_TYPE.READ_ENTITY_NODE,
  NODE_TYPE.WRITE_ENTITY_NODE,
] as const;

export type NamedNodeType = (typeof NAMED_NODE_TYPES)[number];

export const FULLSCREEN_NODE_TYPES: NODE_TYPE[] = [
  NODE_TYPE.DECISION_TABLE_NODE,
  NODE_TYPE.SCORECARD_NODE,
  NODE_TYPE.MATRIX_TABLE_NODE,
  NODE_TYPE.CODE_NODE,
];

export const CONNECT_NODE_TYPES = [
  NODE_TYPE.INTEGRATION_NODE,
  NODE_TYPE.CUSTOM_CONNECTION_NODE,
  NODE_TYPE.WEBHOOK_CONNECTION_NODE,
  NODE_TYPE.SQL_DATABASE_CONNECTION_NODE,
  NODE_TYPE.REVIEW_CONNECTION_NODE,
  NODE_TYPE.CASE_CREATION_NODE,
  NODE_TYPE.CASE_UPDATE_NODE,
] as const;

export type ConnectNodeType = (typeof CONNECT_NODE_TYPES)[number];

/**
 * All the node types that are directly creatable by the user via the AddNodePane
 * Per default all named nodes except special nodes such as input/output nodes
 */
export const CREATABLE_NODE_TYPES = NAMED_NODE_TYPES.filter(
  (n) =>
    ![
      NODE_TYPE.INPUT_NODE,
      NODE_TYPE.OUTPUT_NODE,
      NODE_TYPE.SPLIT_BRANCH_NODE_V2,
      NODE_TYPE.SPLIT_MERGE_NODE,
    ].includes(n),
);

/**
 * All the node types that inject a child flow into the parent flow
 */
export const PARENT_FLOW_NODE_TYPES = [
  NODE_TYPE.FLOW_NODE,
  NODE_TYPE.LOOP_NODE,
];

/**
 * Add Node Group Types
 */
export enum AddNodeGroupType {
  ENTITY_ACTIONS = "entity_actions",
}

export const AddNodeGroupTypes = Object.keys(AddNodeGroupType).map(
  (key) => AddNodeGroupType[key as keyof typeof AddNodeGroupType],
);

export const ADD_NODES_GROUPS = {
  [AddNodeGroupType.ENTITY_ACTIONS]: [
    NODE_TYPE.WRITE_ENTITY_NODE,
    NODE_TYPE.READ_ENTITY_NODE,
  ],
} as const;

/**
 * Connection Group Types - for connection-based groups (requires selecting a connection)
 */
export enum ConnectionGroupType {
  CASE_ACTIONS = "case_actions",
}

export const ConnectionGroupTypes = Object.keys(ConnectionGroupType).map(
  (key) => ConnectionGroupType[key as keyof typeof ConnectionGroupType],
);
